import { useDialogStore } from '@ariakit/react'
import type { FC } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Edit02Icon, Expand01Icon, Image01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types.ts'
import { useUpdateExperiencVettingCoverPhoto } from '@/features/experience-vetting/queries/use-update-experienc-vetting-cover-photo'
import {
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle,
	OpsVettingNoCoverPhotoContainer
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import { ExpVettingCriteriaChoosePhotoDialog } from './exp-vetting-criteria-choose-photo-dialog'

interface ExperienceVettingCriteriaCoverPhotoProps {
	coverPhoto?: ExperienceGalleryImage
	hideAction: boolean
	experienceId: number
	galleryId: number
	onClickExpand?: () => void
}

export const ExperienceVettingCriteriaCoverPhoto: FC<
	ExperienceVettingCriteriaCoverPhotoProps
> = ({ coverPhoto, experienceId, hideAction, galleryId, onClickExpand }) => {
	const { t } = useTranslation()

	const dialog = useDialogStore()
	const { mutate: updateExperienceCoverPhoto, isPending } =
		useUpdateExperiencVettingCoverPhoto({
			experienceId,
			galleryId
		})

	const handleConfirm = (id: number) => {
		updateExperienceCoverPhoto({ isCover: true, imageId: id })
	}
	return (
		<>
			{coverPhoto ? (
				<OpsVettingGalleryItemsContainer direction={'column'} gap={4}>
					<GalleryImageItem
						bottomRightSlot={
							!hideAction && (
								<ButtonLegacy
									className='show-on-hover'
									onClick={onClickExpand}
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<Expand01Icon />
								</ButtonLegacy>
							)
						}
						src={coverPhoto.thumbnails?.xs || coverPhoto.url}
						topRightSlot={
							!hideAction && (
								<ButtonLegacy
									disabled={isPending}
									onClick={() => dialog.toggle()}
									shape='square'
									type='button'
									variant={'secondary'}
								>
									{isPending ? <Spinner /> : <Edit02Icon />}
								</ButtonLegacy>
							)
						}
					/>
					<OpsVettingImageTitle>
						{t('vetting:criteria.commons.gallery.cover_photo')}
					</OpsVettingImageTitle>
				</OpsVettingGalleryItemsContainer>
			) : (
				<OpsVettingGalleryItemsContainer>
					<OpsVettingNoCoverPhotoContainer
						align={'center'}
						direction={'column'}
						gap={2}
						justify={'center'}
					>
						{isPending ? (
							<Spinner size={32} />
						) : (
							<Image01Icon size={32} />
						)}
						<div className='flex flex-col items-center'>
							<h4>
								{t(
									'vetting:criteria.service.gallery.no_cover_photo'
								)}
							</h4>
							{!hideAction && (
								<ButtonLegacy
									disabled={isPending}
									onClick={dialog.toggle}
									size={'sm'}
									type='button'
									variant={'tertiary'}
								>
									{t('common:choose_a_photo')}
								</ButtonLegacy>
							)}
						</div>
					</OpsVettingNoCoverPhotoContainer>
				</OpsVettingGalleryItemsContainer>
			)}

			<ExpVettingCriteriaChoosePhotoDialog
				defaultSelectedImageId={coverPhoto?.id}
				experienceId={experienceId}
				galleryId={galleryId}
				onConfirm={handleConfirm}
				store={dialog}
			/>
		</>
	)
}
